import { zodResolver } from "@hookform/resolvers/zod";
import type { FieldError, FieldErrors, Resolver } from "react-hook-form";
import dayjs from "dayjs";
import z from "zod";

const REQUIRED_MESSAGE = "この項目は必須です。";
const DATE_ORDER_MESSAGE = "回答納期は日時より後の日を選択してください。";

export const RoomInformationSchema = z.object({
  title: z
    .string({ message: REQUIRED_MESSAGE })
    .trim()
    .min(1, REQUIRED_MESSAGE),
  start_date: z
    .string({ message: REQUIRED_MESSAGE })
    .min(1, REQUIRED_MESSAGE),
  end_date: z
    .string({ message: REQUIRED_MESSAGE })
    .min(1, REQUIRED_MESSAGE),
  theme_type: z.object(
    { label: z.string(), value: z.any() },
    { message: REQUIRED_MESSAGE }
  ), // object option
  theme: z.object(
    { label: z.string(), value: z.any() },
    { message: REQUIRED_MESSAGE }
  ), // object option
  viewer_ids: z
    .array(
      z.object({ label: z.string().nullable().optional(), value: z.number() })
    )
    .optional(), // array of options
  description: z
    .string({ message: REQUIRED_MESSAGE })
    .trim()
    .min(1, REQUIRED_MESSAGE)
    .max(1000, "1000文字以内で入力してください。"),
  image: z.array(z.object({ file: z.any(), url: z.string().optional() })), // array of file item, file item: { file, url, id }
});

export type RoomInformationFormData = z.infer<typeof RoomInformationSchema>;

const toFieldError = (message: string): FieldError => ({
  type: "custom",
  message,
});

/**
 * Merge date-order errors with required/other field errors so they show together.
 * Date order is checked independently — other fields do not need to be filled first.
 */
export const roomInformationResolver: Resolver<RoomInformationFormData> = async (
  values,
  context,
  options
) => {
  const fullResult = await zodResolver(RoomInformationSchema)(
    values,
    context,
    options
  );

  const errors: FieldErrors<RoomInformationFormData> = {
    ...fullResult.errors,
  };

  const startDate = values.start_date ? dayjs(values.start_date) : null;
  const endDate = values.end_date ? dayjs(values.end_date) : null;
  if (startDate?.isValid() && endDate?.isValid() && endDate.isBefore(startDate, "day")) {
    errors.end_date = toFieldError(DATE_ORDER_MESSAGE);
  }

  if (Object.keys(errors).length > 0) {
    return { values: {}, errors };
  }

  return fullResult;
};
